import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StaffOnlyDatePicker } from './StaffOnlyDatePicker';
import type { StaffOnlyFormData } from './StaffOnlyFilterForm';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { RESERVABLE_TIME_UNIT_IN_MINUTES } from '@/constants/space';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { getJstDateInfo } from '@/utils/businessDate';
import { floorToTimeUnit, parseTimeToMinutes } from '@/utils/timeUtils';

interface StaffOnlyTimeRangeSelectorProps {
  register: UseFormRegister<StaffOnlyFormData>;
  setValue: UseFormSetValue<StaffOnlyFormData>;
  selectedSpaceType: string;
  selectedDate: string;
  startsAtHour: string;
  startsAtMinute: string;
  endsAtHour: string;
  endsAtMinute: string;
  defaultExpanded?: boolean;
}

export const StaffOnlyTimeRangeSelector = ({
  register,
  setValue,
  selectedSpaceType,
  selectedDate,
  startsAtHour,
  startsAtMinute,
  endsAtHour,
  endsAtMinute,
  defaultExpanded = false,
}: StaffOnlyTimeRangeSelectorProps) => {
  const { t } = useTranslation();
  const { formatDateWithWeekday } = useDateTimeFormat();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isDisabled = !selectedSpaceType;

  const allHours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, '0'),
  );

  // 分の選択肢（予約の最小の時間単位を使用）
  const timeUnit = Math.min(...Object.values(RESERVABLE_TIME_UNIT_IN_MINUTES));
  const allMinutes = Array.from({ length: 60 / timeUnit }, (_, i) =>
    (i * timeUnit).toString().padStart(2, '0'),
  );

  // 終了時間の選択肢をフィルタリング（開始時刻より後のみ）
  const getAvailableEndHours = (): string[] => {
    const startMinutes = parseTimeToMinutes(startsAtHour, startsAtMinute);
    if (startMinutes === null) return allHours;

    // 開始時刻と同じ時間以降を表示（分で調整）
    const startHour = parseInt(startsAtHour, 10);
    return allHours.filter((hour) => parseInt(hour, 10) >= startHour);
  };

  // 終了分の選択肢をフィルタリング（同じ時間の場合は開始分より後のみ）
  const getAvailableEndMinutes = (): string[] => {
    if (!endsAtHour || !startsAtHour) return allMinutes;

    const startHour = parseInt(startsAtHour, 10);
    const endHour = parseInt(endsAtHour, 10);

    // 終了時間が開始時間より大きい場合は全ての分を選択可能
    if (endHour > startHour) return allMinutes;

    // 同じ時間の場合は開始分より大きい分のみ選択可能
    if (endHour === startHour) {
      const startMinute = startsAtMinute ? parseInt(startsAtMinute, 10) : 0;
      return allMinutes.filter((minute) => parseInt(minute, 10) > startMinute);
    }

    return allMinutes;
  };

  // 開始時刻変更時のハンドラー
  const handleStartHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStartHour = e.target.value;
    setValue('startsAtHour', newStartHour);

    // 時を「--」にしたら分も「--」にする
    if (!newStartHour) {
      setValue('startsAtMinute', '');
      return;
    }

    // 終了時刻が開始時刻より前になる場合はクリア
    if (newStartHour && endsAtHour) {
      const newStartMinutes =
        parseInt(newStartHour, 10) * 60 +
        (startsAtMinute ? parseInt(startsAtMinute, 10) : 0);
      const endMinutes = parseTimeToMinutes(endsAtHour, endsAtMinute);
      if (endMinutes !== null && endMinutes <= newStartMinutes) {
        setValue('endsAtHour', '');
        setValue('endsAtMinute', '');
      }
    }
  };

  // 開始分変更時のハンドラー
  const handleStartMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStartMinute = e.target.value;
    setValue('startsAtMinute', newStartMinute);

    // 分を「--」にしたら時も「--」にする
    if (!newStartMinute) {
      setValue('startsAtHour', '');
      return;
    }

    // 終了時刻が開始時刻より前または同じになる場合はクリア
    if (startsAtHour && endsAtHour) {
      const newStartMinutes =
        parseInt(startsAtHour, 10) * 60 +
        (newStartMinute ? parseInt(newStartMinute, 10) : 0);
      const endMinutes = parseTimeToMinutes(endsAtHour, endsAtMinute);
      if (endMinutes !== null && endMinutes <= newStartMinutes) {
        setValue('endsAtHour', '');
        setValue('endsAtMinute', '');
      }
    }
  };

  // 終了時刻変更時のハンドラー
  const handleEndHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newEndHour = e.target.value;
    setValue('endsAtHour', newEndHour);

    // 時を「--」にしたら分も「--」にする
    if (!newEndHour) {
      setValue('endsAtMinute', '');
      return;
    }

    // 終了分が無効になる場合はクリア
    if (newEndHour && startsAtHour && endsAtMinute) {
      const startHour = parseInt(startsAtHour, 10);
      const endHour = parseInt(newEndHour, 10);
      if (endHour === startHour) {
        const startMinute = startsAtMinute ? parseInt(startsAtMinute, 10) : 0;
        const endMinute = parseInt(endsAtMinute, 10);
        if (endMinute <= startMinute) {
          setValue('endsAtMinute', '');
        }
      }
    }
  };

  // 終了分変更時のハンドラー
  const handleEndMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newEndMinute = e.target.value;
    setValue('endsAtMinute', newEndMinute);

    // 分を「--」にしたら時も「--」にする
    if (!newEndMinute) {
      setValue('endsAtHour', '');
    }
  };

  // 日付選択時のハンドラー
  const handleDateSelect = (date: string) => {
    if (selectedDate === date) {
      setValue('searchDate', '');
      return;
    }

    setValue('searchDate', date);
  };

  // 選択された日付のサマリー表示
  const getDateSummary = (): string => {
    if (!selectedDate) {
      return t('staffOnly.timeRange.dateNotSelected');
    }

    return formatDateWithWeekday(selectedDate);
  };

  // ヘッダーに表示するラベル
  const getHeaderLabel = (): string => {
    if (!selectedDate && !startsAtHour && !endsAtHour) {
      return t('staffOnly.timeRange.title');
    }

    const parts: string[] = [];
    if (selectedDate) {
      parts.push(getDateSummary());
    }
    if (startsAtHour || endsAtHour) {
      const startTime =
        startsAtHour && startsAtMinute
          ? `${startsAtHour}:${startsAtMinute}`
          : '--:--';
      const endTime =
        endsAtHour && endsAtMinute ? `${endsAtHour}:${endsAtMinute}` : '--:--';
      parts.push(`${startTime} ～ ${endTime}`);
    }
    return parts.join(' ');
  };

  // ヘッダーがfilledかどうか
  const isHeaderFilled = !!selectedDate || !!startsAtHour || !!endsAtHour;

  return (
    <div
      className={`e__field e-search__field e-search__field--datetime ${isExpanded ? 'is-open' : ''}`}
    >
      <div
        className={`e-search__field-header ${isHeaderFilled ? 'is-filled' : ''}`}
        onClick={() => {
          if (isDisabled) return;

          // 展開時に開始時間が未指定であれば、デフォルトの選択は現在時刻とする
          // ※折りたたみ時（isExpanded が true の時）は設定しない
          if (!isExpanded && !startsAtHour && !startsAtMinute) {
            const { hour, minute } = getJstDateInfo(new Date());
            setValue('startsAtHour', hour.toString().padStart(2, '0'));
            setValue(
              'startsAtMinute',
              floorToTimeUnit(minute, timeUnit).toString().padStart(2, '0'),
            );
          }

          setIsExpanded(!isExpanded);
        }}
        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
      >
        <div className="e-search__field-label">{getHeaderLabel()}</div>
      </div>

      <div className="e-search__field-body">
        {/* 日付ピッカー */}
        <StaffOnlyDatePicker
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
        />

        <input type="hidden" {...register('searchDate')} />

        {/* タイムゾーン補足 */}
        <div className="e-search__timezone">
          {t('staffOnly.timeRange.timezoneNote')}
        </div>

        {/* 日付・時間帯選択ブロック */}
        <div className="e-datetime">
          {/* 選択された日付の表示 */}
          <div className="e-datetime__date-summary" aria-live="polite">
            {getDateSummary()}
          </div>

          {/* 検索時間：開始 ～ 終了 */}
          <div className={'e-datetime__range is-enabled'}>
            <div className="e-datetime__range-label">
              {t('staffOnly.timeRange.searchTime')}
            </div>

            <div className="e-datetime__range-body">
              {/* 開始時間 */}
              <div className="e-datetime__time e-datetime__time--start">
                <div className="e-datetime__time-unit">
                  <div className="e-datetime__time-unit-label">
                    {t('staffOnly.timeRange.hour')}
                  </div>
                  <div className="e-datetime__time-unit-control">
                    <select
                      value={startsAtHour}
                      onChange={handleStartHourChange}
                      className="e-datetime__select e-datetime__select--hour"
                    >
                      <option value="">--</option>
                      {allHours.map((hour) => (
                        <option key={`start-hour-${hour}`} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="e-datetime__time-separator">:</div>

                <div className="e-datetime__time-unit">
                  <div className="e-datetime__time-unit-label">
                    {t('staffOnly.timeRange.minute')}
                  </div>
                  <div className="e-datetime__time-unit-control">
                    <select
                      value={startsAtMinute}
                      onChange={handleStartMinuteChange}
                      className="e-datetime__select e-datetime__select--minute"
                    >
                      <option value="">--</option>
                      {allMinutes.map((minute) => (
                        <option key={`start-minute-${minute}`} value={minute}>
                          {minute}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="e-datetime__range-separator">
                {t('staffOnly.timeRange.separator')}
              </div>

              {/* 終了時間 */}
              <div className="e-datetime__time e-datetime__time--end">
                <div className="e-datetime__time-unit">
                  <div className="e-datetime__time-unit-label">
                    {t('staffOnly.timeRange.hour')}
                  </div>
                  <div className="e-datetime__time-unit-control">
                    <select
                      value={endsAtHour}
                      onChange={handleEndHourChange}
                      className="e-datetime__select e-datetime__select--hour"
                    >
                      <option value="">--</option>
                      {getAvailableEndHours().map((hour) => (
                        <option key={`end-hour-${hour}`} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="e-datetime__time-separator">:</div>

                <div className="e-datetime__time-unit">
                  <div className="e-datetime__time-unit-label">
                    {t('staffOnly.timeRange.minute')}
                  </div>
                  <div className="e-datetime__time-unit-control">
                    <select
                      value={endsAtMinute}
                      onChange={handleEndMinuteChange}
                      className="e-datetime__select e-datetime__select--minute"
                    >
                      <option value="">--</option>
                      {getAvailableEndMinutes().map((minute) => (
                        <option key={`end-minute-${minute}`} value={minute}>
                          {minute}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
